/**
 * The readable curve — the chart a reader is allowed to take a value off.
 *
 * Where `CresultcardSparkline` answers only "which way", this one answers
 * "how much, when". That upgrade costs three things, and all three are here
 * rather than in eighteen pillar files: labelled x endpoints, a threshold at
 * which the curve changes what it MEANS (and therefore its tone), and a
 * crosshair that reports the reading under the pointer.
 *
 * The crosshair is the part with teeth. Its position is written straight to
 * `element.style.transform` from the pointer handler — no transition, no CSS
 * custom property, no state round-trip — because a crosshair that arrives a
 * frame after the pointer is not a crosshair, it is a lagging decoration. Only
 * the TEXT goes through React: the tooltip fades in on first contact, fades out
 * more slowly on release, and its values re-enter as the index changes so a
 * reader can see that the number they are looking at is a new one.
 *
 * The tooltip rides the crosshair but is NOT centred on it. It uses the kit's
 * offset-then-counter-translate placement — sit at the reading's fraction, then
 * translate back by that same fraction of the box's OWN width — which is the one
 * placement that is exactly centred mid-plot and still cannot leave either edge
 * of the chart. Centring it instead put half the box outside the plot at the
 * ends, where a pinned crosshair spends most of its life.
 *
 * The secondary series is inset half a column deliberately. A column series
 * drawn edge-to-edge under a line puts point *i* over the BOUNDARY between
 * column *i* and column *i+1*, which silently misattributes every reading by
 * half a period; here point *i* sits over column *i*'s centre because both
 * share one x scale built from the column count.
 */
"use client";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
} from "react";
import {
  CrcAnimatedProperty,
  CrcDuration,
  CrcEase,
  crcTransition,
} from "./cresultcard.motion";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import {
  CRC_CORE_BACKGROUND_COLOR,
  CRC_CORE_SHADOW,
  CRC_DIVIDER,
  CRC_NUMERIC_STYLE,
  CRC_STACK_GAP,
  CRC_TEXT,
  CRC_TEXT_FAINT,
  CRC_TEXT_MUTED,
  CRC_TONE_COLOR,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import {
  CresultcardCrosshair,
  CresultcardStackDensity,
  CresultcardTextStep,
  CresultcardTone,
} from "./cresultcard.types";
import { useCresultcardViewport } from "./cresultcard.viewport";

/* ── Geometry ─────────────────────────────────────────────────────────────── */

/** The plot's own coordinate space, stretched to the container at paint time. */
const AREA_VIEW_WIDTH = 100;
const AREA_VIEW_HEIGHT = 60;
/** Half the stroke, so the extremes are not clipped by the viewBox edge. */
const AREA_INSET = 2;
const AREA_STROKE_WIDTH = 1.8;
const AREA_FILL_OPACITY = 0.15;
const COLUMN_OPACITY = 0.34;
/** The share of a column slot left empty, so the columns read as separate. */
const COLUMN_GAP_RATIO = 0.34;
/** Half a slot — the inset that puts point *i* over column *i*'s centre. */
const HALF_SLOT = 0.5;
const THRESHOLD_DASH = "2 3";
const THRESHOLD_STROKE_WIDTH = 1;
/** A flat series sits on the mid-line: on the floor it would read as a crash. */
const FLAT_SERIES_LEVEL = 0.5;
const PATH_PRECISION = 2;
const MIN_PLOTTABLE_POINTS = 2;
const PERCENT = 100;

const PATH_MOVE = "M";
const PATH_LINE = "L";
const PATH_CLOSE = "Z";
const PATH_JOIN = " ";

/** The stroke must not thicken when the box is stretched to the container. */
const NON_SCALING_STROKE = "non-scaling-stroke";

/** The plot's block size. No chart takes a WIDTH; a height it must have. */
const AREA_PLOT_HEIGHT = 148;
const AREA_PLOT_HEIGHT_MOBILE = 116;

const CROSSHAIR_WIDTH = 1;
const CROSSHAIR_DOT_DIAMETER = 7;
const CROSSHAIR_DOT_RING = 2;
const TOOLTIP_INSET = 8;
const TOOLTIP_PAD_BLOCK = 5;
const TOOLTIP_PAD_INLINE = 8;
/**
 * Where the tooltip sits when nothing is under the pointer. It is empty and on
 * its way out at that moment, so it holds the centre rather than sliding to an
 * edge while it fades.
 */
const TOOLTIP_RESTING_RATIO = 0.5;
/** The axis tick's hairline. It fills the axis row's own padding, no more. */
const AXIS_TICK_MARK_WIDTH = 1;
const ANIMATION_FILL_BOTH = "both";
const TOUCH_ACTION_NONE = "none";
const USER_SELECT_NONE = "none";
const CURSOR_SCRUB = "ew-resize";
/** The one keyframe in `Cresultcard.css` a re-entering value can borrow. */
const VALUE_ENTER_KEYFRAME = "cresultcard-fade-in";
/** `useId` emits colons, which are not legal inside a `url(#…)` reference. */
const ID_UNSAFE = /:/g;

/**
 * Which tone the curve swaps to on the far side of the threshold. It is a map
 * rather than a second prop because the swap is a PAIRING inside the tone
 * vocabulary — above target / below target, gain / loss — and a free colour
 * choice at the call site is how eighteen pillars end up with eighteen reds.
 */
const COUNTER_TONE: Record<CresultcardTone, CresultcardTone> = {
  [CresultcardTone.Positive]: CresultcardTone.Negative,
  [CresultcardTone.Negative]: CresultcardTone.Positive,
  [CresultcardTone.Caution]: CresultcardTone.Muted,
  [CresultcardTone.Neutral]: CresultcardTone.Muted,
  [CresultcardTone.Muted]: CresultcardTone.Neutral,
};

enum AreaKey {
  ArrowLeft = "ArrowLeft",
  ArrowRight = "ArrowRight",
  Home = "Home",
  End = "End",
}

/* ── Public shapes ────────────────────────────────────────────────────────── */

/**
 * One intermediate mark on the x axis, keyed to the READING it names rather
 * than to a fraction of the width. A tick placed by hand drifts off the point
 * it labels the moment the series length or the column count changes, and a
 * tick that does not sit over its own reading is worse than no tick at all.
 */
export interface CresultcardAreaChartTick {
  readonly index: number;
  readonly label: string;
}

/** The two labelled x endpoints. An unlabelled axis is an unreadable chart. */
export interface CresultcardAreaChartBounds {
  readonly start: string;
  readonly end: string;
  /**
   * Marks between the two endpoints — 00 / 06 / 12 / 18 on a day of hours. A
   * chart long enough that its ends stop answering "when" needs interior marks
   * to stay readable; a short one does not, so this is optional and an axis
   * without it is the two-label axis it has always been.
   */
  readonly ticks?: readonly CresultcardAreaChartTick[];
}

/** One column of the secondary series, sharing the primary series' x axis. */
export interface CresultcardAreaChartColumn {
  readonly label: string;
  readonly value: number;
}

/** What the crosshair is currently over. */
export interface CresultcardAreaChartReading {
  readonly index: number;
  readonly value: number;
}

/**
 * The tooltip's rendered text. Strings rather than nodes so the same content
 * can be handed to `aria-valuetext` without a second formatter.
 */
export interface CresultcardAreaChartTooltip {
  readonly primary: string;
  readonly secondary?: string;
}

export type CresultcardAreaChartTooltipFormatter = (
  reading: CresultcardAreaChartReading,
) => CresultcardAreaChartTooltip;

/** A stable empty list, so the column branch never re-narrows a maybe-prop. */
const NO_COLUMNS: readonly CresultcardAreaChartColumn[] = [];
/** The same, for an axis whose caller named only its two ends. */
const NO_TICKS: readonly CresultcardAreaChartTick[] = [];

function defaultAreaTooltip({
  value,
}: CresultcardAreaChartReading): CresultcardAreaChartTooltip {
  return { primary: String(value) };
}

/* ── Plot construction ────────────────────────────────────────────────────── */

interface AreaPlot {
  /** x of point *i* as a 0–1 share of the plot's width. */
  readonly ratios: readonly number[];
  /** y of point *i* as a 0–1 share of the plot's height, measured from the top. */
  readonly levels: readonly number[];
  readonly line: string;
  /** The fill down to the floor — the shape when there is no threshold. */
  readonly area: string;
  /**
   * The fill down to the THRESHOLD rather than the floor. Clipping that band
   * above and below the threshold is what makes the swap mean "the series
   * crossed the level"; clipping the floor-anchored fill instead would just
   * paint a horizontal stripe across a curve that never crossed anything.
   */
  readonly bandArea: string | null;
  /** The threshold's y in viewBox units, or null when none was given. */
  readonly thresholdY: number | null;
}

interface AreaPlotInput {
  readonly points: readonly number[];
  readonly threshold?: number;
  /** Zero when there is no secondary series, which means edge-to-edge x. */
  readonly columnCount: number;
}

/**
 * Point *i*'s x as a share of the width. With columns present every point sits
 * over its own column's CENTRE; without them the curve spans edge to edge, so
 * the first and last readings land on the labelled bounds.
 */
function xRatioOf(index: number, count: number, columnCount: number): number {
  if (columnCount > 0) {
    return (index + HALF_SLOT) / columnCount;
  }
  return count < MIN_PLOTTABLE_POINTS ? HALF_SLOT : index / (count - 1);
}

function buildAreaPlot({
  points,
  threshold,
  columnCount,
}: AreaPlotInput): AreaPlot {
  if (points.length < MIN_PLOTTABLE_POINTS) {
    return {
      ratios: [],
      levels: [],
      line: "",
      area: "",
      bandArea: null,
      thresholdY: null,
    };
  }

  // The threshold joins the domain: a target line drawn outside the plotted
  // range is a line the reader never sees, which is worse than no line.
  const domain = threshold == null ? points : [...points, threshold];
  const lowest = Math.min(...domain);
  const highest = Math.max(...domain);
  const span = highest - lowest;
  const levelOf = (value: number): number =>
    span === 0 ? FLAT_SERIES_LEVEL : 1 - (value - lowest) / span;

  const ratios = points.map((_point, index): number =>
    xRatioOf(index, points.length, columnCount),
  );
  const levels = points.map((point): number => levelOf(point));

  const plotHeight = AREA_VIEW_HEIGHT - AREA_INSET * 2;
  const line = levels
    .map((level, index): string => {
      const command = index === 0 ? PATH_MOVE : PATH_LINE;
      const x = ratios[index] * AREA_VIEW_WIDTH;
      const y = AREA_INSET + level * plotHeight;
      return `${command}${x.toFixed(PATH_PRECISION)} ${y.toFixed(PATH_PRECISION)}`;
    })
    .join(PATH_JOIN);

  const firstX = (ratios[0] * AREA_VIEW_WIDTH).toFixed(PATH_PRECISION);
  const lastX = (
    ratios[ratios.length - 1] * AREA_VIEW_WIDTH
  ).toFixed(PATH_PRECISION);
  const floor = String(AREA_VIEW_HEIGHT);
  const thresholdY =
    threshold == null ? null : AREA_INSET + levelOf(threshold) * plotHeight;
  const baseline = thresholdY == null ? null : thresholdY.toFixed(PATH_PRECISION);

  return {
    ratios,
    levels,
    line,
    area: `${line} ${PATH_LINE}${lastX} ${floor} ${PATH_LINE}${firstX} ${floor} ${PATH_CLOSE}`,
    bandArea:
      baseline == null
        ? null
        : `${line} ${PATH_LINE}${lastX} ${baseline} ${PATH_LINE}${firstX} ${baseline} ${PATH_CLOSE}`,
    thresholdY,
  };
}

interface NearestIndexInput {
  readonly ratio: number;
  readonly ratios: readonly number[];
}

function nearestIndex({ ratio, ratios }: NearestIndexInput): number {
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  ratios.forEach((candidate, index): void => {
    const distance = Math.abs(candidate - ratio);
    if (distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
  });
  return best;
}

function translateXOf(offset: number): string {
  return `translate3d(${offset.toFixed(PATH_PRECISION)}px, 0, 0)`;
}

/** A 0–1 share of a width, as the percentage a style value can carry. */
function percentOf(ratio: number): string {
  return `${(ratio * PERCENT).toFixed(PATH_PRECISION)}%`;
}

interface AxisTickPlacement {
  readonly key: string;
  readonly label: string;
  /** The tick's x as a share of the axis width — the plot's own scale. */
  readonly offset: string;
}

interface AxisTickPlacementInput {
  readonly ticks: readonly CresultcardAreaChartTick[];
  readonly ratios: readonly number[];
}

/**
 * Ticks placed on the SAME x scale the curve was built from, so a mark sits
 * over the reading it names whether or not a column series inset the points by
 * half a slot. A tick naming an index the chart never plotted has no position
 * on this axis and is dropped: labelling a point that is not there is the one
 * outcome worse than an unmarked axis.
 */
function placeAxisTicks({
  ticks,
  ratios,
}: AxisTickPlacementInput): readonly AxisTickPlacement[] {
  return ticks
    .filter((tick): boolean => ratios[tick.index] != null)
    .map(
      (tick): AxisTickPlacement => ({
        key: `${tick.label}-${String(tick.index)}`,
        label: tick.label,
        offset: percentOf(ratios[tick.index]),
      }),
    );
}

const RTL_DIRECTION = "rtl";

/**
 * +1 when the inline axis runs left-to-right, −1 when it runs the other way.
 * `getBoundingClientRect` and `transform` are the two places in this file that
 * are unavoidably physical, so the direction is read from the element rather
 * than assumed: in an RTL block the crosshair would otherwise walk away from
 * the pointer and index 0 would sit under the LAST reading.
 */
function inlineSign(element: HTMLElement): number {
  return window.getComputedStyle(element).direction === RTL_DIRECTION
    ? -1
    : 1;
}

/* ── The two clipped copies of one series ─────────────────────────────────── */

interface AreaSeriesProps {
  readonly area: string;
  readonly line: string;
  readonly color: string;
  /** A `url(#…)` reference, or undefined when the series is not split. */
  readonly clipReference?: string;
}

function CresultcardAreaSeries({
  area,
  line,
  color,
  clipReference,
}: AreaSeriesProps): ReactElement {
  return (
    <g
      clipPath={clipReference}
      data-el="Cresultcard/AreaSeries/Root"
    >
      <path
        d={area}
        fill={color}
        fillOpacity={AREA_FILL_OPACITY}
        stroke="none"
      />
      <path
        className="cresultcard__path"
        d={line}
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={AREA_STROKE_WIDTH}
        vectorEffect={NON_SCALING_STROKE}
      />
    </g>
  );
}

/* ── The chart ────────────────────────────────────────────────────────────── */

interface CresultcardAreaChartProps {
  readonly points: readonly number[];
  readonly tone: CresultcardTone;
  /** The level at which the stroke and the fill swap to the counter tone. */
  readonly threshold?: number;
  /** A column series on the same x axis, inset half a column. */
  readonly secondarySeries?: readonly CresultcardAreaChartColumn[];
  readonly bounds: CresultcardAreaChartBounds;
  readonly crosshair: CresultcardCrosshair;
  readonly tooltipFormatter?: CresultcardAreaChartTooltipFormatter;
  readonly onScrub?: (reading: CresultcardAreaChartReading) => void;
  /** The scrubber's accessible name — a slider without one is unusable. */
  readonly label: string;
}

export function CresultcardAreaChart({
  points,
  tone,
  threshold,
  secondarySeries,
  bounds,
  crosshair,
  tooltipFormatter = defaultAreaTooltip,
  onScrub,
  label,
}: CresultcardAreaChartProps): ReactElement {
  const { isMobile } = useCresultcardViewport();
  const radius = useCresultcardNestedRadius();
  const clipId = useId().replace(ID_UNSAFE, "");
  const plotRef = useRef<HTMLDivElement | null>(null);
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const reportedRef = useRef<number | null>(null);

  const tracking = crosshair !== CresultcardCrosshair.Off;
  const pinned = crosshair === CresultcardCrosshair.Pinned;
  const lastIndex = points.length - 1;
  const columns = secondarySeries ?? NO_COLUMNS;
  const columnCount = columns.length;
  const plottable = points.length >= MIN_PLOTTABLE_POINTS;

  const [activeIndex, setActiveIndex] = useState<number | null>(
    pinned && lastIndex >= 0 ? lastIndex : null,
  );

  const plot = useMemo<AreaPlot>(
    (): AreaPlot => buildAreaPlot({ points, threshold, columnCount }),
    [columnCount, points, threshold],
  );

  const activeRatio =
    activeIndex == null ? null : (plot.ratios[activeIndex] ?? null);

  const declaredTicks = bounds.ticks ?? NO_TICKS;
  const plotRatios = plot.ratios;
  const axisTicks = useMemo<readonly AxisTickPlacement[]>(
    (): readonly AxisTickPlacement[] =>
      placeAxisTicks({ ratios: plotRatios, ticks: declaredTicks }),
    [declaredTicks, plotRatios],
  );

  // Keeps the crosshair honest for the paths React owns — mount, keyboard, a
  // changed series. The pointer path writes the same transform itself, one
  // frame earlier, so this re-write is idempotent rather than a second clock.
  useEffect((): void => {
    const plotBox = plotRef.current;
    const cursor = cursorRef.current;
    if (plotBox == null || cursor == null || activeRatio == null) {
      return;
    }
    const width = plotBox.getBoundingClientRect().width;
    cursor.style.transform = translateXOf(
      inlineSign(plotBox) * activeRatio * width,
    );
  }, [activeRatio]);

  const moveToIndex = useCallback(
    (index: number): void => {
      const plotBox = plotRef.current;
      const cursor = cursorRef.current;
      if (plotBox != null && cursor != null) {
        const width = plotBox.getBoundingClientRect().width;
        cursor.style.transform = translateXOf(
          inlineSign(plotBox) * plot.ratios[index] * width,
        );
      }
      setActiveIndex(index);
      if (onScrub != null && reportedRef.current !== index) {
        reportedRef.current = index;
        onScrub({ index, value: points[index] });
      }
    },
    [onScrub, plot, points],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>): void => {
      const plotBox = plotRef.current;
      if (plotBox == null || !plottable) {
        return;
      }
      const rect = plotBox.getBoundingClientRect();
      if (rect.width === 0) {
        return;
      }
      const physical = (event.clientX - rect.left) / rect.width;
      const ratio = inlineSign(plotBox) === 1 ? physical : 1 - physical;
      moveToIndex(nearestIndex({ ratio, ratios: plot.ratios }));
    },
    [moveToIndex, plot, plottable],
  );

  const handlePointerLeave = useCallback((): void => {
    if (!pinned) {
      setActiveIndex(null);
      reportedRef.current = null;
    }
  }, [pinned]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (!plottable) {
        return;
      }
      const current = activeIndex ?? 0;
      if (event.key === AreaKey.ArrowRight) {
        event.preventDefault();
        moveToIndex(Math.min(current + 1, lastIndex));
        return;
      }
      if (event.key === AreaKey.ArrowLeft) {
        event.preventDefault();
        moveToIndex(Math.max(current - 1, 0));
        return;
      }
      if (event.key === AreaKey.Home) {
        event.preventDefault();
        moveToIndex(0);
        return;
      }
      if (event.key === AreaKey.End) {
        event.preventDefault();
        moveToIndex(lastIndex);
      }
    },
    [activeIndex, lastIndex, moveToIndex, plottable],
  );

  const primaryColor = CRC_TONE_COLOR[tone];
  const counterColor = CRC_TONE_COLOR[COUNTER_TONE[tone]];
  const thresholdY = plot.thresholdY;
  const bandArea = plot.bandArea ?? plot.area;
  const aboveReference = `url(#${clipId}-above)`;
  const belowReference = `url(#${clipId}-below)`;

  const columnPeak =
    columnCount === 0
      ? 0
      : Math.max(...columns.map((column): number => column.value), 0);

  const reading: CresultcardAreaChartReading | null =
    activeIndex == null || !plottable
      ? null
      : { index: activeIndex, value: points[activeIndex] };
  const tooltip = reading == null ? null : tooltipFormatter(reading);
  const tooltipVisible = tooltip != null;
  const tooltipSecondary = tooltip?.secondary;
  // Offset-then-counter-translate: the box already sits at the reading's
  // fraction of the plot (it rides the crosshair), so translating it back by
  // that same fraction of its OWN width leaves it centred in the middle of the
  // plot, flush with the leading edge at the start and flush with the trailing
  // edge at the end. No measurement, and no edge it can cross.
  const tooltipShift = percentOf(-(activeRatio ?? TOOLTIP_RESTING_RATIO));
  const valueText =
    tooltip == null
      ? undefined
      : tooltipSecondary == null
        ? tooltip.primary
        : `${tooltip.primary}${PATH_JOIN}${tooltipSecondary}`;

  return (
    <div
      data-el="Cresultcard/AreaChart/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: CRC_STACK_GAP[CresultcardStackDensity.Tight],
        width: "100%",
        minWidth: 0,
      }}
    >
      <div
        data-el="Cresultcard/AreaChart/Plot"
        ref={plotRef}
        style={{
          position: "relative",
          width: "100%",
          height: isMobile ? AREA_PLOT_HEIGHT_MOBILE : AREA_PLOT_HEIGHT,
        }}
      >
        <svg
          aria-hidden
          preserveAspectRatio="none"
          style={{ display: "block", width: "100%", height: "100%" }}
          viewBox={`0 0 ${String(AREA_VIEW_WIDTH)} ${String(AREA_VIEW_HEIGHT)}`}
        >
          {thresholdY != null && (
            <defs
            >
              <clipPath
                id={`${clipId}-above`}
              >
                <rect
                  height={thresholdY}
                  width={AREA_VIEW_WIDTH}
                  x={0}
                  y={0}
                />
              </clipPath>
              <clipPath
                id={`${clipId}-below`}
              >
                <rect
                  height={AREA_VIEW_HEIGHT - thresholdY}
                  width={AREA_VIEW_WIDTH}
                  x={0}
                  y={thresholdY}
                />
              </clipPath>
            </defs>
          )}

          {columns.map((column, index): ReactElement => {
            const slot = AREA_VIEW_WIDTH / columnCount;
            const barWidth = slot * (1 - COLUMN_GAP_RATIO);
            const share = columnPeak === 0 ? 0 : column.value / columnPeak;
            const barHeight = share * (AREA_VIEW_HEIGHT - AREA_INSET);
            return (
              <rect
                fill={CRC_TEXT_FAINT}
                fillOpacity={COLUMN_OPACITY}
                height={barHeight}
                key={`${column.label}-${String(index)}`}
                width={barWidth}
                x={index * slot + (slot - barWidth) / 2}
                y={AREA_VIEW_HEIGHT - barHeight}
              />
            );
          })}

          {plottable && thresholdY == null && (
            <CresultcardAreaSeries
              area={plot.area}
              color={primaryColor}
              line={plot.line}
            />
          )}
          {plottable && thresholdY != null && (
            <>
              <CresultcardAreaSeries
                area={bandArea}
                clipReference={aboveReference}
                color={primaryColor}
                line={plot.line}
              />
              <CresultcardAreaSeries
                area={bandArea}
                clipReference={belowReference}
                color={counterColor}
                line={plot.line}
              />
              <line
                stroke={CRC_TEXT_MUTED}
                strokeDasharray={THRESHOLD_DASH}
                strokeWidth={THRESHOLD_STROKE_WIDTH}
                vectorEffect={NON_SCALING_STROKE}
                x1={0}
                x2={AREA_VIEW_WIDTH}
                y1={thresholdY}
                y2={thresholdY}
              />
            </>
          )}
        </svg>

        {tracking && (
          <div
            ref={cursorRef}
            style={{
              position: "absolute",
              insetBlock: 0,
              insetInlineStart: 0,
              width: 0,
              pointerEvents: "none",
              willChange: "transform",
            }}
          >
            <span
              style={{
                position: "absolute",
                insetBlock: 0,
                insetInlineStart: 0,
                width: CROSSHAIR_WIDTH,
                backgroundColor: CRC_TEXT_MUTED,
                opacity: tooltipVisible ? 1 : 0,
                transition: crcTransition({
                  properties: [CrcAnimatedProperty.Opacity],
                  duration: tooltipVisible ? CrcDuration.Tap : CrcDuration.Enter,
                  ease: CrcEase.Settle,
                }),
              }}
            />
            {activeIndex != null && plot.levels[activeIndex] != null && (
              <span
                style={{
                  position: "absolute",
                  insetInlineStart: 0,
                  insetBlockStart: percentOf(plot.levels[activeIndex]),
                  width: CROSSHAIR_DOT_DIAMETER,
                  height: CROSSHAIR_DOT_DIAMETER,
                  borderRadius: CROSSHAIR_DOT_DIAMETER,
                  backgroundColor: primaryColor,
                  boxShadow: `0 0 0 ${String(CROSSHAIR_DOT_RING)}px ${CRC_CORE_BACKGROUND_COLOR}`,
                  transform: "translate3d(-50%, -50%, 0)",
                }}
              />
            )}
            <span
              data-el="Cresultcard/AreaChart/Tooltip"
              style={{
                position: "absolute",
                insetBlockStart: TOOLTIP_INSET,
                insetInlineStart: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                paddingBlock: TOOLTIP_PAD_BLOCK,
                paddingInline: TOOLTIP_PAD_INLINE,
                borderRadius: radius,
                backgroundColor: CRC_CORE_BACKGROUND_COLOR,
                boxShadow: CRC_CORE_SHADOW,
                whiteSpace: "nowrap",
                opacity: tooltipVisible ? 1 : 0,
                transform: `translate3d(${tooltipShift}, 0, 0)`,
                transition: crcTransition({
                  properties: [CrcAnimatedProperty.Opacity],
                  duration: tooltipVisible ? CrcDuration.Tap : CrcDuration.Enter,
                  ease: CrcEase.Settle,
                }),
              }}
            >
              <span
                key={activeIndex ?? lastIndex}
                style={{
                  ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
                  ...CRC_NUMERIC_STYLE,
                  color: CRC_TEXT,
                  animationName: VALUE_ENTER_KEYFRAME,
                  animationDuration: CrcDuration.Tap,
                  animationTimingFunction: CrcEase.Settle,
                  animationFillMode: ANIMATION_FILL_BOTH,
                }}
              >
                {tooltip?.primary}
              </span>
              {tooltipSecondary != null && (
                <span
                  style={{
                    ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
                    ...CRC_NUMERIC_STYLE,
                    color: CRC_TEXT_MUTED,
                  }}
                >
                  {tooltipSecondary}
                </span>
              )}
            </span>
          </div>
        )}

        {tracking && (
          <div
            aria-label={label}
            aria-valuemax={Math.max(lastIndex, 0)}
            aria-valuemin={0}
            aria-valuenow={activeIndex ?? 0}
            aria-valuetext={valueText}
            data-crc-control
            onKeyDown={handleKeyDown}
            onPointerDown={handlePointerMove}
            onPointerLeave={handlePointerLeave}
            onPointerMove={handlePointerMove}
            role="slider"
            style={{
              position: "absolute",
              inset: 0,
              cursor: CURSOR_SCRUB,
              touchAction: TOUCH_ACTION_NONE,
              userSelect: USER_SELECT_NONE,
            }}
            tabIndex={0}
          />
        )}
      </div>

      <div
        data-el="Cresultcard/AreaChart/Axis"
        style={{
          // The interior ticks hang off this row rather than off the plot, so
          // the row is their containing block. It has no positioned siblings to
          // reorder and no z-index, so an axis without ticks is untouched by it.
          position: "relative",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: CRC_STACK_GAP[CresultcardStackDensity.Snug],
          borderTop: `1px solid ${CRC_DIVIDER}`,
          paddingBlockStart: CRC_STACK_GAP[CresultcardStackDensity.Tight],
        }}
      >
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            ...CRC_NUMERIC_STYLE,
            textAlign: "start",
          }}
        >
          {bounds.start}
        </span>
        <span
          style={{
            ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
            ...CRC_NUMERIC_STYLE,
            textAlign: "end",
          }}
        >
          {bounds.end}
        </span>
        {axisTicks.map(
          (tick): ReactElement => (
            <span
              key={tick.key}
              style={{
                position: "absolute",
                insetBlockStart: 0,
                insetInlineStart: tick.offset,
                // A zero-width column centres both of its children on the tick's
                // own x, in either direction, without a counter-translate the
                // inline axis would have to flip.
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: 0,
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: AXIS_TICK_MARK_WIDTH,
                  // The row's own padding, so the mark spans the gap between the
                  // axis rule and the label and the row does not grow.
                  height: CRC_STACK_GAP[CresultcardStackDensity.Tight],
                  backgroundColor: CRC_TEXT_FAINT,
                }}
              />
              <span
                style={{
                  ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
                  ...CRC_NUMERIC_STYLE,
                  whiteSpace: "nowrap",
                }}
              >
                {tick.label}
              </span>
            </span>
          ),
        )}
      </div>
    </div>
  );
}
